/**
 * Multi-Touch Attribution Analytics Page
 *
 * Customer journey analysis with Sankey diagrams and conversion path tables.
 * Tabs: Flow by Source/Campaign, Retargeting by Source/Campaign, Conversion Paths
 *       (Conversion Paths has optional campaign filter after source selection)
 */

import { useState, useEffect, useCallback } from 'react'
import { format, subDays } from 'date-fns'
import { trafficAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { DateRangePicker } from '../components/DateRangePicker'
import PaymentFreshnessStrip from '../components/PaymentFreshnessStrip'
import SankeyDiagram, { SankeySummary } from '../components/SankeyDiagram'
import PathsTable from '../components/PathsTable'
import AttributionModels from '../components/AttributionModels'
import PathUsersModal from '../components/PathUsersModal'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Button, Badge,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui'
import { RefreshCw, Loader2, AlertCircle, TrendingUp, Info } from 'lucide-react'

// Tab configuration
const TABS = [
  // Attribution Models leads, because it answers the question the rest of the
  // page decorates: what the same sales look like under each credit rule. The
  // flow and path views are how you go and check one.
  { id: 'models', label: 'Attribution Models', type: 'models' },
  { id: 'flow-source', label: 'Flow by Source', type: 'flow', groupBy: 'source' },
  { id: 'flow-campaign', label: 'Flow by Campaign', type: 'flow', groupBy: 'campaign' },
  { id: 'retarget-source', label: 'Retargeting by Source', type: 'retargeting', groupBy: 'source' },
  { id: 'retarget-campaign', label: 'Retargeting by Campaign', type: 'retargeting', groupBy: 'campaign' },
  { id: 'paths', label: 'Conversion Paths', type: 'paths' },
]

// Get default date range (last 30 days)
function getDefaultDateRange() {
  const end = new Date()
  const start = subDays(end, 29)
  return { from: start, to: end }
}

export default function AttributionPage() {
  const { currentClinicId } = useAuthStore()

  // State
  const hasClinic = Boolean(currentClinicId)
  // Models is the landing tab where it exists. It is Science-only today, and a
  // clinic that cannot see it must not land on a tab that is filtered out of
  // its own tab bar.
  const [activeTab, setActiveTab] = useState(hasClinic ? 'models' : 'flow-source')
  const [modelsRefresh, setModelsRefresh] = useState(0)
  const [dateRange, setDateRange] = useState(getDefaultDateRange)

  // Switching to a clinic without the Models tab would otherwise leave the page
  // on a tab its own tab bar no longer offers, rendering nothing.
  useEffect(() => {
    setActiveTab(current => (current === 'models' && !hasClinic ? 'flow-source' : current))
  }, [hasClinic])
  const [selectedFilter, setSelectedFilter] = useState('') // Source filter for paths tab
  const [selectedCampaign, setSelectedCampaign] = useState('') // Optional campaign filter for paths tab
  const [selectedProduct, setSelectedProduct] = useState('') // For product filter on retargeting
  const [pathUsersItem, setPathUsersItem] = useState(null) // Path whose user IDs are being viewed

  // Data state
  const [flowData, setFlowData] = useState(null)
  const [retargetData, setRetargetData] = useState(null)
  const [pathsData, setPathsData] = useState(null)
  const [filterOptions, setFilterOptions] = useState({ sources: [], campaigns: [], products: [], sourceCampaigns: {} })

  // Loading states
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Get current tab config
  const currentTab = TABS.find(t => t.id === activeTab)

  // Format dates for API
  const formatDateForAPI = (date) => format(date, 'yyyy-MM-dd')

  // Fetch filter options (sources/campaigns)
  const fetchFilterOptions = useCallback(async () => {
    try {
      const params = {
        start: formatDateForAPI(dateRange.from),
        end: formatDateForAPI(dateRange.to),
      }
      const response = await trafficAPI.getAttributionFilters(params)
      if (response.data?.data) {
        setFilterOptions(response.data.data)
      }
    } catch (err) {
      console.error('Failed to fetch filter options:', err)
    }
  }, [dateRange])

  // Fetch flow data (Start -> Sources -> End)
  const fetchFlowData = useCallback(async (groupBy) => {
    setLoading(true)
    setError(null)
    try {
      const params = {
        start: formatDateForAPI(dateRange.from),
        end: formatDateForAPI(dateRange.to),
        groupBy,
      }
      // Add product filter if selected
      if (selectedProduct) {
        params.product = selectedProduct
      }
      const response = await trafficAPI.getAttributionFlows(params)
      if (response.data?.data) {
        setFlowData(response.data.data)
      }
    } catch (err) {
      console.error('Failed to fetch flow data:', err)
      setError(err.response?.data?.error?.message || 'Failed to load attribution flow data')
    } finally {
      setLoading(false)
    }
  }, [dateRange, selectedProduct])

  // Fetch retargeting data (multi-touch sequences)
  const fetchRetargetData = useCallback(async (groupBy) => {
    setLoading(true)
    setError(null)
    try {
      const params = {
        start: formatDateForAPI(dateRange.from),
        end: formatDateForAPI(dateRange.to),
        groupBy,
        minTouches: 2, // Hardcoded - show all multi-touch journeys
      }
      // Add product filter if selected
      if (selectedProduct) {
        params.product = selectedProduct
      }
      const response = await trafficAPI.getRetargeting(params)
      if (response.data?.data) {
        setRetargetData(response.data.data)
      }
    } catch (err) {
      console.error('Failed to fetch retargeting data:', err)
      setError(err.response?.data?.error?.message || 'Failed to load retargeting data')
    } finally {
      setLoading(false)
    }
  }, [dateRange, selectedProduct])

  // Fetch paths data
  const fetchPathsData = useCallback(async () => {
    if (!selectedFilter) {
      setPathsData(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const params = {
        start: formatDateForAPI(dateRange.from),
        end: formatDateForAPI(dateRange.to),
        filterType: 'source',
        filterValue: selectedFilter,
      }
      // Add optional campaign filter
      if (selectedCampaign) {
        params.campaign = selectedCampaign
      }
      const response = await trafficAPI.getAttributionPaths(params)
      if (response.data?.data) {
        setPathsData(response.data.data)
      }
    } catch (err) {
      console.error('Failed to fetch paths data:', err)
      setError(err.response?.data?.error?.message || 'Failed to load paths data')
    } finally {
      setLoading(false)
    }
  }, [dateRange, selectedFilter, selectedCampaign])

  // Fetch data based on active tab
  const fetchData = useCallback(() => {
    if (!currentTab) return

    if (currentTab.type === 'flow') {
      fetchFlowData(currentTab.groupBy)
    } else if (currentTab.type === 'retargeting') {
      fetchRetargetData(currentTab.groupBy)
    } else if (currentTab.type === 'paths') {
      fetchPathsData()
    }
  }, [currentTab, fetchFlowData, fetchRetargetData, fetchPathsData])

  // Fetch filter options on date range change
  useEffect(() => {
    fetchFilterOptions()
  }, [fetchFilterOptions])

  // Fetch data when tab changes or date range changes
  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Fetch paths when source or campaign filter changes
  useEffect(() => {
    if (currentTab?.type === 'paths' && selectedFilter) {
      fetchPathsData()
    }
  }, [selectedFilter, selectedCampaign])

  // Reset campaign filter when source changes
  useEffect(() => {
    setSelectedCampaign('')
  }, [selectedFilter])

  // Reset filters when switching to paths tab
  useEffect(() => {
    if (currentTab?.type === 'paths') {
      setSelectedFilter('')
      setSelectedCampaign('')
      setPathsData(null)
    }
  }, [activeTab])

  // Handle refresh
  const handleRefresh = () => {
    fetchData()
    setModelsRefresh(value => value + 1)
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Multi-Touch Attribution</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Analyze customer journeys and conversion paths
          </p>
        </div>
        <div className="flex items-center gap-3">
          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      <PaymentFreshnessStrip dateRange={dateRange} />

      {/* Sales Team is read from core's price book, not from pageviews — an
          agent closing a customer leaves no trace in Segment. It is added as a
          final touch so the channel that closed the sale is visible, which
          means a journey showing a Sales Team touch has one fewer pageview
          behind it than touches. Every other channel here counts real events. */}
      {currentTab?.type !== 'models' && <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 flex-none" />
        <span>
          <span className="font-medium text-foreground">Sales Team</span> touches come from
          core&apos;s price book, not from tracked pageviews — an agent closing a customer
          leaves no Segment event. They are added as the closing touch, so a path containing
          one has fewer pageviews behind it than it shows touches.
        </span>
      </div>}

      {/* Error Alert */}
      {error && currentTab?.type !== 'models' && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-destructive">Error loading data</h3>
            <p className="text-sm text-destructive/80 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="flex gap-1 overflow-x-auto pb-px">
          {TABS.filter(tab => tab.type !== 'models' || hasClinic).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors
                border-b-2 -mb-px
                ${activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {currentTab?.type === 'models' && (
          <AttributionModels key={currentClinicId} dateRange={dateRange} refresh={modelsRefresh} />
        )}
        {/* Flow Tabs (1 & 2) */}
        {currentTab?.type === 'flow' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Attribution Flow by {currentTab.groupBy === 'source' ? 'Source' : 'Campaign'}
                  </CardTitle>
                  <CardDescription>
                    Traffic flow from all sources to conversion outcomes
                  </CardDescription>
                </div>
                {filterOptions.products?.length > 0 && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-muted-foreground">Product:</label>
                    <Select
                      value={selectedProduct || 'all'}
                      onValueChange={(v) => setSelectedProduct(v === 'all' ? '' : v)}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="All Products" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Products</SelectItem>
                        {filterOptions.products.map((product) => (
                          <SelectItem key={product} value={product}>
                            {product}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {flowData?.summary && (
                <SankeySummary summary={flowData.summary} />
              )}
              <SankeyDiagram
                data={flowData}
                type="flow"
                height={500}
                loading={loading}
                emptyMessage="No attribution data found for the selected date range"
              />
            </CardContent>
          </Card>
        )}

        {/* Retargeting Tabs (3 & 4) */}
        {currentTab?.type === 'retargeting' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Retargeting Flow by {currentTab.groupBy === 'source' ? 'Source' : 'Campaign'}
                  </CardTitle>
                  <CardDescription>
                    Multi-touch journey sequences showing retargeting effectiveness
                  </CardDescription>
                </div>
                {filterOptions.products?.length > 0 && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-muted-foreground">Product:</label>
                    <Select
                      value={selectedProduct || 'all'}
                      onValueChange={(v) => setSelectedProduct(v === 'all' ? '' : v)}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="All Products" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Products</SelectItem>
                        {filterOptions.products.map((product) => (
                          <SelectItem key={product} value={product}>
                            {product}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {retargetData?.stats && (
                <SankeySummary summary={retargetData.stats} />
              )}
              <SankeyDiagram
                data={retargetData}
                type="retargeting"
                height={500}
                loading={loading}
                emptyMessage="No multi-touch journeys found for the selected filters"
              />
            </CardContent>
          </Card>
        )}

        {/* Conversion Paths Tab */}
        {currentTab?.type === 'paths' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Conversion Paths
                  </CardTitle>
                  <CardDescription>
                    Detailed journey paths and conversion rates
                  </CardDescription>
                </div>
                <div className="flex items-center gap-4">
                  {/* Source Filter */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-muted-foreground">Source:</label>
                    <Select
                      value={selectedFilter}
                      onValueChange={setSelectedFilter}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Choose source" />
                      </SelectTrigger>
                      <SelectContent>
                        {filterOptions.sources.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Campaign Filter - only shown after source is selected, scoped to that source */}
                  {selectedFilter && filterOptions.sourceCampaigns?.[selectedFilter]?.length > 0 && (
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-muted-foreground">Campaign:</label>
                      <Select
                        value={selectedCampaign || 'all'}
                        onValueChange={(v) => setSelectedCampaign(v === 'all' ? '' : v)}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="All campaigns" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All campaigns</SelectItem>
                          {filterOptions.sourceCampaigns[selectedFilter].map((campaign) => (
                            <SelectItem key={campaign} value={campaign}>
                              {campaign}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {!selectedFilter ? (
                <div className="text-center py-12 text-muted-foreground">
                  <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Select a source to view conversion paths</p>
                </div>
              ) : (
                <PathsTable
                  paths={pathsData?.paths || []}
                  summary={pathsData?.summary}
                  loading={loading}
                  emptyMessage={`No conversion paths found for ${selectedFilter}${selectedCampaign ? ` / ${selectedCampaign}` : ''}`}
                  onViewUsers={setPathUsersItem}
                />
              )}
            </CardContent>
          </Card>
        )}

        {/* Path Users Modal - user IDs behind a conversion path */}
        <PathUsersModal
          isOpen={!!pathUsersItem}
          onClose={() => setPathUsersItem(null)}
          pathItem={pathUsersItem}
          queryParams={{
            start: formatDateForAPI(dateRange.from),
            end: formatDateForAPI(dateRange.to),
            filterType: 'source',
            filterValue: selectedFilter,
            ...(selectedCampaign ? { campaign: selectedCampaign } : {})
          }}
        />
      </div>

      {/* Footer note */}
      <div className="text-xs text-muted-foreground text-center">
        Data from Segment page views. Customer journeys tracked via anonymous_id.
        Conversions based on payment_succeeded events.
      </div>
    </div>
  )
}
